
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useBlogPost } from "@/hooks/useBlogPost";
import EnhancedBlogPreview from "@/components/blog-management/EnhancedBlogPreview";
import { useEffect, useState } from "react";

const BlogPostPreviewPage = () => {
  const { slug, id } = useParams<{ slug?: string; id?: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  // Use either slug or id to fetch the post
  const postIdentifier = slug || id || "";
  const queryKey = slug ? "slug" : "id";
  
  // Only attempt to fetch if we have a valid identifier
  const hasValidIdentifier = Boolean(postIdentifier && postIdentifier !== "new");
  
  const { data: post, error, isLoading: isFetching } = useBlogPost(
    postIdentifier, 
    queryKey,
    { enabled: hasValidIdentifier }
  );
  
  useEffect(() => {
    // Set loading to false after a short delay to show preview
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [post]);

  // Handle back button click
  const handleBack = () => {
    navigate("/dashboard/blog/posts");
  };

  // Convert post data to format expected by EnhancedBlogPreview
  const formatPostForPreview = (post) => {
    if (!post) return null;
    
    return {
      ...post,
      // Convert the string date to a Date object if it exists
      published_at: post.published_at ? new Date(post.published_at) : null
    };
  };

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBack}
        >
          <ArrowLeft size={18} />
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">
          Post Preview
        </h1>
      </div>

      {/* Loading state */}
      {(isLoading || isFetching) && (
        <Card className="p-8 flex justify-center items-center">
          <div className="flex flex-col items-center">
            <Loader2 size={24} className="animate-spin text-slate-600 mb-2" />
            <p className="text-slate-600">Loading post preview...</p>
          </div>
        </Card>
      )}

      {/* Error state */}
      {error && !isLoading && hasValidIdentifier && (
        <Card className="p-8 bg-red-50">
          <div className="text-center">
            <h2 className="text-lg font-semibold text-red-600 mb-2">Error Loading Preview</h2>
            <p className="text-red-600 mb-4">
              The post could not be loaded. It may have been deleted or you may not have permission to view it.
            </p>
            <Button variant="outline" onClick={handleBack}>
              Back to Posts
            </Button>
          </div>
        </Card>
      )}

      {/* Show invalid ID message if trying to preview with invalid ID */}
      {!hasValidIdentifier && !isLoading && (
        <Card className="p-8 bg-yellow-50">
          <div className="text-center">
            <h2 className="text-lg font-semibold text-yellow-600 mb-2">No Post to Preview</h2>
            <p className="text-yellow-600 mb-4">
              No valid post identifier was provided. Please select a post to preview.
            </p>
            <Button variant="outline" onClick={handleBack}>
              Back to Posts
            </Button>
          </div>
        </Card>
      )}

      {/* Preview content */}
      {post && !isLoading && !error && (
        <Card className="p-6">
          <EnhancedBlogPreview post={formatPostForPreview(post)} />
        </Card>
      )}
    </div>
  );
};

export default BlogPostPreviewPage;
